import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";

export type AppSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type AppSelectProps = {
  id?: string;
  name?: string;
  value?: string;
  defaultValue?: string;
  options: readonly AppSelectOption[];
  onValueChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  size?: "md" | "lg";
  tone?: "surface" | "soft";
  className?: string;
};

type PopupPosition = {
  left: number;
  top?: number;
  bottom?: number;
  width: number;
  openUpwards: boolean;
};

const POPUP_GAP = 8;
const POPUP_MAX_HEIGHT = 320;

export function AppSelect({
  id,
  name,
  value,
  defaultValue,
  options,
  onValueChange,
  placeholder = "Sélectionner",
  disabled = false,
  ariaLabel,
  ariaLabelledBy,
  size = "md",
  tone = "surface",
  className,
}: AppSelectProps) {
  const generatedId = useId();
  const controlId = id ?? `app-select-${generatedId}`;
  const listboxId = `${controlId}-listbox`;
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [popupPosition, setPopupPosition] = useState<PopupPosition | null>(null);
  const isControlled = value !== undefined;
  const firstEnabledValue = useMemo(
    () => options.find((option) => !option.disabled)?.value ?? "",
    [options],
  );
  const [internalValue, setInternalValue] = useState(defaultValue ?? firstEnabledValue);
  const selectedValue = isControlled ? value : internalValue;
  const selectedOption = options.find((option) => option.value === selectedValue) ?? null;
  const [highlightedIndex, setHighlightedIndex] = useState(() => findSelectableIndex(options, selectedValue));

  useEffect(() => {
    if (!isControlled && !options.some((option) => option.value === internalValue)) {
      setInternalValue(defaultValue ?? firstEnabledValue);
    }
  }, [defaultValue, firstEnabledValue, internalValue, isControlled, options]);

  useEffect(() => {
    setHighlightedIndex(findSelectableIndex(options, selectedValue));
  }, [options, selectedValue]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (
        target &&
        (buttonRef.current?.contains(target) || listRef.current?.contains(target))
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleWindowBlur = () => {
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    window.addEventListener("blur", handleWindowBlur);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return;

    const updatePopupPosition = () => {
      const button = buttonRef.current;
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUpwards = spaceBelow < POPUP_MAX_HEIGHT && spaceAbove > spaceBelow;
      setPopupPosition({
        left: rect.left,
        width: rect.width,
        top: openUpwards ? undefined : rect.bottom + POPUP_GAP,
        bottom: openUpwards ? window.innerHeight - rect.top + POPUP_GAP : undefined,
        openUpwards,
      });
    };

    updatePopupPosition();
    window.addEventListener("resize", updatePopupPosition);
    window.addEventListener("scroll", updatePopupPosition, true);
    return () => {
      window.removeEventListener("resize", updatePopupPosition);
      window.removeEventListener("scroll", updatePopupPosition, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const index = findSelectableIndex(options, selectedValue);
    setHighlightedIndex(index);
  }, [isOpen, options, selectedValue]);

  useEffect(() => {
    if (!isOpen || highlightedIndex < 0) return;
    const activeOption = listRef.current?.querySelector<HTMLElement>(`[data-option-index="${highlightedIndex}"]`);
    activeOption?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, isOpen]);

  const shellClassName = [
    "app-select",
    `app-select--${size}`,
    `app-select--${tone}`,
    disabled ? "is-disabled" : "",
    isOpen ? "is-open" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  const commitValue = (nextValue: string) => {
    if (!isControlled) {
      setInternalValue(nextValue);
    }
    onValueChange?.(nextValue);
    setIsOpen(false);
    window.requestAnimationFrame(() => {
      buttonRef.current?.focus();
    });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled || !options.length) return;

    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          return;
        }
        setHighlightedIndex((current) => getNextSelectableIndex(options, current, 1));
        return;
      }
      case "ArrowUp": {
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          return;
        }
        setHighlightedIndex((current) => getNextSelectableIndex(options, current, -1));
        return;
      }
      case "Enter":
      case " ": {
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          return;
        }
        const option = options[highlightedIndex];
        if (option && !option.disabled) {
          commitValue(option.value);
        }
        return;
      }
      case "Escape": {
        if (isOpen) {
          event.preventDefault();
          setIsOpen(false);
        }
        return;
      }
      case "Tab": {
        setIsOpen(false);
        return;
      }
      default:
        return;
    }
  };

  return (
    <>
      <div className={shellClassName}>
        <input type="hidden" name={name} value={selectedValue ?? ""} />
        <button
          ref={buttonRef}
          id={controlId}
          type="button"
          className="app-select__trigger"
          disabled={disabled || options.length === 0}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabel ? undefined : ariaLabelledBy}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? listboxId : undefined}
          onClick={() => setIsOpen((current) => !current)}
          onKeyDown={handleKeyDown}
        >
          <span className={`app-select__value${selectedOption ? "" : " is-placeholder"}`}>
            {selectedOption?.label ?? placeholder}
          </span>
          <span className="app-select__icon" aria-hidden="true">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="m5 7 5 6 5-6" />
            </svg>
          </span>
        </button>
      </div>

      {isOpen && popupPosition
        ? createPortal(
            <div
              ref={listRef}
              id={listboxId}
              className={`app-select__popup${popupPosition.openUpwards ? " is-open-upwards" : ""}`}
              role="listbox"
              aria-labelledby={ariaLabel ? undefined : ariaLabelledBy}
              style={{
                left: popupPosition.left,
                width: popupPosition.width,
                top: popupPosition.top,
                bottom: popupPosition.bottom,
                maxHeight: POPUP_MAX_HEIGHT,
              }}
            >
              {options.map((option, index) => {
                const isSelected = option.value === selectedValue;
                const isHighlighted = index === highlightedIndex;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    data-option-index={index}
                    className={[
                      "app-select__option",
                      isSelected ? "is-selected" : "",
                      isHighlighted ? "is-highlighted" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    disabled={option.disabled}
                    onMouseEnter={() => {
                      if (!option.disabled) setHighlightedIndex(index);
                    }}
                    onClick={() => {
                      if (!option.disabled) commitValue(option.value);
                    }}
                  >
                    <span>{option.label}</span>
                    {isSelected ? (
                      <span className="app-select__check" aria-hidden="true">
                        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="m4 10 4 4 8-8" />
                        </svg>
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function findSelectableIndex(options: readonly AppSelectOption[], selectedValue: string | undefined) {
  const selectedIndex = options.findIndex((option) => option.value === selectedValue && !option.disabled);
  if (selectedIndex >= 0) return selectedIndex;
  return options.findIndex((option) => !option.disabled);
}

function getNextSelectableIndex(
  options: readonly AppSelectOption[],
  currentIndex: number,
  step: 1 | -1,
) {
  if (!options.length) return -1;

  let index = currentIndex >= 0 ? currentIndex : step > 0 ? -1 : options.length;
  for (let count = 0; count < options.length; count += 1) {
    index = (index + step + options.length) % options.length;
    if (!options[index]?.disabled) return index;
  }
  return currentIndex;
}
